/**
 * Saying Yes to "Grow thinks this is …" in a plant picker: the plant chosen,
 * made first when Grow does not have it.
 *
 * - One Grow has by another name (known) is chosen as it is; when only the crop was named ("garlic", typeUnsure) its Type is
 *   "Not sure" (B52), the crop and the flag, never the usual type's id.
 * - Under a broad flower parent the flower becomes a variety of the
 *   gardener's own by its name ("Bee Balm" under Perennial Flower; POST
 *   /plants/own), as every flower without a kind is filed.
 * - Under any other plant it becomes the gardener's own plant, in that
 *   plant's group, cared for like it (POST /plants/own).
 *
 * The crop and the variety a known plant starts on are the server's: the varieties answer of the plant Grow named (GET
 * /plants/picker/varieties, read through the cache; it names the crop and says what Variety starts on). An own plant sits in
 * the group of the plant it went under, so it is the same crop. The picker's answers are marked stale afterwards, so the
 * new plant or variety is in every picker.
 *
 * Who uses it: DescribePlant (PlantIdentityFields, PileCard). Caching: reads the varieties answer under
 * ['plant-picker', 'varieties', garden, language, plantId]; filing marks every ['plant-picker'] answer stale.
 *
 * @module useFileSuggestion
 */

import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { fileOwnPlant, type PlantSuggestion } from '@/shared/api/plantNames';
import { pickerStale, usePickerScope, varietiesQuery, type CropChoice } from './usePlantPicker';

/**
 * A name as a plant or variety is named: each word's first letter raised ("bee balm" is Bee Balm).
 *
 * @param name - The name.
 */
export const titleName = (name: string): string => name.replace(/(^|\s)(\p{L})/gu, (_whole, space: string, letter: string) => space + letter.toLocaleUpperCase());

/**
 * Files a suggestion and answers the choice it amounts to.
 *
 * @returns file(suggestion): the crop, plant and variety to choose.
 */
export function useFileSuggestion(): (suggestion: PlantSuggestion) => Promise<CropChoice> {
  const queryClient = useQueryClient();
  const { gardenId, language, signedIn } = usePickerScope();
  return React.useCallback(async (suggestion: PlantSuggestion): Promise<CropChoice> => {
    // The plant Grow named: its crop, and where its Variety starts, as the server says.
    const named = await queryClient.fetchQuery(varietiesQuery({ gardenId, language, signedIn }, suggestion.plantId));
    const cropId = named.crop.id;
    if (suggestion.known) {
      if (suggestion.typeUnsure) return { cropId, plantId: null, varietyId: null, typeUnsure: true };
      return { cropId, plantId: suggestion.plantId, varietyId: named.start.varietyId };
    }
    const filed = await fileOwnPlant(titleName(suggestion.name), suggestion.plantId);
    await pickerStale(queryClient);
    // An own plant sits in the group of the plant it went under: the same crop.
    return { cropId, plantId: filed.plantId, varietyId: filed.varietyId };
  }, [queryClient, gardenId, language, signedIn]);
}
